import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

// Uygulama içi "beğendin mi?" akışında MEMNUN OLMAYAN kullanıcıların geri
// bildirimleri (mağazaya gitmez). 'Harika' seçenler native mağaza yorumuna
// yönlenir; buraya yalnızca 'Kötü' / 'Orta' seçenler düşer.
const DEGERLENDIRME_RENK = {
  "Kötü": "danger",
  "Orta": "warning",
};

const formatTarih = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleString("tr-TR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const UygulamaGeriBildirimleri = () => {
  document.title = "Uygulama Deneyimleri | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [liste, setListe] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [detay, setDetay] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchListe = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/UYGULAMA_GERI_BILDIRIMLERI?$orderby=ID desc`, { headers });
      const result = await res.json();
      setListe(result.value || []);
    } catch {
      setError("Geri bildirimler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchListe(); }, []);

  const openDetay = (row) => {
    setDetay(row);
    setModal(true);
    // Okunmamışsa açılınca okundu olarak işaretle.
    if (!row.OKUNDU) markRead(row.ID, true, false);
  };

  const markRead = async (id, okundu, showMsg = true) => {
    try {
      await fetch(`${BASE_URL}/UYGULAMA_GERI_BILDIRIMLERI(${id})`, {
        method: "PATCH", headers, body: JSON.stringify({ OKUNDU: okundu }),
      });
      setListe(prev => prev.map(x => x.ID === id ? { ...x, OKUNDU: okundu } : x));
      if (showMsg) {
        setSuccessMsg(okundu ? "Okundu olarak işaretlendi." : "Okunmadı olarak işaretlendi.");
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch {
      setError("İşaretleme sırasında hata oluştu.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu geri bildirimi silmek istediğinize emin misiniz?")) return;
    setIsSaving(true);
    try {
      await fetch(`${BASE_URL}/UYGULAMA_GERI_BILDIRIMLERI(${id})`, { method: "DELETE", headers });
      setSuccessMsg("Geri bildirim silindi.");
      setListe(prev => prev.filter(x => x.ID !== id));
      setModal(false);
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const okunmamisSayi = useMemo(() => liste.filter(x => !x.OKUNDU).length, [liste]);

  const columns = useMemo(() => [
    {
      header: "#",
      accessorKey: "ID",
      enableSorting: true,
      cell: (c) => <strong>#{c.getValue()}</strong>,
    },
    {
      header: "Durum",
      accessorKey: "OKUNDU",
      enableSorting: true,
      cell: (c) => c.getValue()
        ? <Badge color="light" className="text-dark">Okundu</Badge>
        : <Badge color="info">Yeni</Badge>,
    },
    {
      header: "Değerlendirme",
      accessorKey: "DEGERLENDIRME",
      enableSorting: true,
      cell: (c) => {
        const v = c.getValue() || "—";
        return <Badge color={DEGERLENDIRME_RENK[v] || "secondary"}>{v}</Badge>;
      },
    },
    {
      header: "Mesaj",
      accessorKey: "MESAJ",
      enableSorting: false,
      cell: (c) => {
        const v = c.getValue() || "";
        return <span className="fw-medium">{v.length > 80 ? v.slice(0, 80) + "…" : (v || "---")}</span>;
      },
    },
    {
      header: "Kullanıcı",
      accessorKey: "KULLANICI_ID",
      enableSorting: true,
      cell: (c) => {
        const v = c.getValue();
        return v ? <small className="text-muted">#{v}</small> : <small className="text-muted">Anonim</small>;
      },
    },
    {
      header: "Platform",
      accessorKey: "PLATFORM",
      enableSorting: true,
      cell: (c) => {
        const v = (c.getValue() || "").toLowerCase();
        if (v === "ios") return <Badge color="dark">iOS</Badge>;
        if (v === "android") return <Badge color="success">Android</Badge>;
        return <span className="text-muted">—</span>;
      },
    },
    {
      header: "Versiyon",
      accessorKey: "UYGULAMA_VERSIYON",
      enableSorting: true,
      cell: (c) => <small className="text-muted">{c.getValue() || "—"}</small>,
    },
    {
      header: "Tarih",
      accessorKey: "OLUSTURMA_TARIHI",
      enableSorting: true,
      cell: (c) => <small className="text-muted">{formatTarih(c.getValue())}</small>,
    },
    {
      header: "İşlemler",
      enableSorting: false,
      cell: (cellProps) => {
        const row = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => openDetay(row)}>
              <i className="bx bx-show" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(row.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Uygulama" breadcrumbItem="Uygulama Deneyimleri" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="mb-3 d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div>
                      <h4 className="card-title mb-1">Uygulama Deneyimleri</h4>
                      <p className="text-muted small mb-0">
                        Kullanıcıların uygulama içi değerlendirme akışında bıraktığı geri bildirimler
                        (memnun olmayanlar). Mutlu kullanıcılar doğrudan mağaza yorumuna yönlenir.
                      </p>
                    </div>
                    {okunmamisSayi > 0 && (
                      <Badge color="info" className="fs-6 align-self-center">
                        {okunmamisSayi} yeni
                      </Badge>
                    )}
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={liste}
                      isGlobalFilter={true}
                      isPagination={true}
                      customPageSize={15}
                      SearchPlaceholder="Geri bildirim ara..."
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination pagination-rounded justify-content-end mb-2"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="lg">
        <ModalHeader toggle={() => setModal(false)}>
          <i className="mdi mdi-message-alert-outline me-2 text-primary"></i>
          Geri Bildirim {detay && `(#${detay.ID})`}
        </ModalHeader>
        <ModalBody>
          {detay && (
            <Row>
              <Col md={12}>
                <div className="d-flex gap-2 mb-3 flex-wrap">
                  <Badge color={DEGERLENDIRME_RENK[detay.DEGERLENDIRME] || "secondary"}>
                    {detay.DEGERLENDIRME || "—"}
                  </Badge>
                  {(detay.PLATFORM || "").toLowerCase() === "ios" && <Badge color="dark">iOS</Badge>}
                  {(detay.PLATFORM || "").toLowerCase() === "android" && <Badge color="success">Android</Badge>}
                  {detay.UYGULAMA_VERSIYON && <Badge color="light" className="text-dark">v{detay.UYGULAMA_VERSIYON}</Badge>}
                  <Badge color="light" className="text-dark">
                    {detay.KULLANICI_ID ? `Kullanıcı #${detay.KULLANICI_ID}` : "Anonim"}
                  </Badge>
                </div>
                <div className="bg-light rounded p-3 mb-2">
                  <strong>Mesaj:</strong>
                  <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{detay.MESAJ || "—"}</p>
                </div>
                <small className="text-muted">{formatTarih(detay.OLUSTURMA_TARIHI)}</small>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          {detay && detay.OKUNDU && (
            <Button color="secondary" outline onClick={() => markRead(detay.ID, false)}>
              <i className="bx bx-envelope me-1" /> Okunmadı işaretle
            </Button>
          )}
          <Button color="danger" outline onClick={() => handleDelete(detay?.ID)} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-trash me-1" />}
            Sil
          </Button>
          <Button color="secondary" onClick={() => setModal(false)}>Kapat</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default UygulamaGeriBildirimleri;
